/**
 * LocalStorage utility module for persisting user preferences across the application
 * Handles user choices for task details, filters, sorting, and other UI preferences
 */

export interface TaskDetailsPreferences {
  commentsExpanded: boolean;
  filesExpanded: boolean;
  commentSortOrder: 'asc' | 'desc';
  editorHeight: number;
  lastCommentContent: string;
}

export interface TaskManagementPreferences {
  searchQuery: string;
  statusFilter: string;
  priorityFilter: string;
  typeFilter: string;
  clientFilter: string;
  sortBy: 'priority' | 'status' | 'createdAt';
  sortOrder: 'asc' | 'desc';
  viewMode: 'table' | 'grid';
  itemsPerPage: number;
}

export interface UIPreferences {
  sidebarMinimized: boolean;
  activeTab: string;
  taskDetailsLayout: 'horizontal' | 'vertical';
  theme: 'light' | 'dark';
}

export interface UserPreferences {
  taskDetails: TaskDetailsPreferences;
  taskManagement: TaskManagementPreferences;
  ui: UIPreferences;
}

// Default preferences
const DEFAULT_PREFERENCES: UserPreferences = {
  taskDetails: {
    commentsExpanded: true,
    filesExpanded: true,
    commentSortOrder: 'desc',
    editorHeight: 150,
    lastCommentContent: '',
  },
  taskManagement: {
    searchQuery: '',
    statusFilter: '',
    priorityFilter: '',
    typeFilter: '',
    clientFilter: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
    viewMode: 'table',
    itemsPerPage: 25,
  },
  ui: {
    sidebarMinimized: false,
    activeTab: 'overview',
    taskDetailsLayout: 'horizontal',
    theme: 'light',
  },
};

// Storage keys
const STORAGE_KEYS = {
  USER_PREFERENCES: 'userPreferences',
  TASK_DETAILS: 'taskDetailsPreferences',
  TASK_MANAGEMENT: 'taskManagementPreferences',
  UI_PREFERENCES: 'uiPreferences',
} as const;

/**
 * Get stored preferences or return defaults
 */
export const getStoredPreferences = (): UserPreferences => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.USER_PREFERENCES);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle new preference fields
      return {
        ...DEFAULT_PREFERENCES,
        ...parsed,
        taskDetails: { ...DEFAULT_PREFERENCES.taskDetails, ...parsed.taskDetails },
        taskManagement: { ...DEFAULT_PREFERENCES.taskManagement, ...parsed.taskManagement },
        ui: { ...DEFAULT_PREFERENCES.ui, ...parsed.ui },
      };
    }
  } catch (error) {
    console.warn('Failed to parse stored preferences:', error);
  }
  return DEFAULT_PREFERENCES;
};

/**
 * Save preferences to localStorage
 */
export const savePreferences = (preferences: UserPreferences): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.USER_PREFERENCES, JSON.stringify(preferences));
  } catch (error) {
    console.error('Failed to save preferences:', error);
  }
};

/**
 * Update specific preference section
 */
export const updatePreferences = <K extends keyof UserPreferences>(
  section: K,
  updates: Partial<UserPreferences[K]>
): void => {
  const current = getStoredPreferences();
  const updated = {
    ...current,
    [section]: { ...current[section], ...updates },
  };
  savePreferences(updated);
};

/**
 * Task Details specific preference functions
 */
export const getTaskDetailsPreferences = (): TaskDetailsPreferences => {
  return getStoredPreferences().taskDetails;
};

export const updateTaskDetailsPreferences = (updates: Partial<TaskDetailsPreferences>): void => {
  updatePreferences('taskDetails', updates);
};

/**
 * Task Management specific preference functions
 */
export const getTaskManagementPreferences = (): TaskManagementPreferences => {
  return getStoredPreferences().taskManagement;
};

export const updateTaskManagementPreferences = (updates: Partial<TaskManagementPreferences>): void => {
  updatePreferences('taskManagement', updates);
};

/**
 * UI specific preference functions
 */
export const getUIPreferences = (): UIPreferences => {
  return getStoredPreferences().ui;
};

export const updateUIPreferences = (updates: Partial<UIPreferences>): void => {
  updatePreferences('ui', updates);
};

/**
 * Clear all preferences (reset to defaults)
 */
export const clearPreferences = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.USER_PREFERENCES);
  } catch (error) {
    console.error('Failed to clear preferences:', error);
  }
};

/**
 * Export preferences for backup/import
 */
export const exportPreferences = (): string => {
  return JSON.stringify(getStoredPreferences(), null, 2);
};

/**
 * Import preferences from backup
 */
export const importPreferences = (preferencesJson: string): boolean => {
  try {
    const preferences = JSON.parse(preferencesJson);
    savePreferences(preferences);
    return true;
  } catch (error) {
    console.error('Failed to import preferences:', error);
    return false;
  }
};

/**
 * Hook for managing preferences in React components
 */
export const usePreferences = () => {
  const getPreferences = () => getStoredPreferences();
  
  const updateTaskDetails = (updates: Partial<TaskDetailsPreferences>) => {
    updateTaskDetailsPreferences(updates);
  };
  
  const updateTaskManagement = (updates: Partial<TaskManagementPreferences>) => {
    updateTaskManagementPreferences(updates);
  };
  
  const updateUI = (updates: Partial<UIPreferences>) => {
    updateUIPreferences(updates);
  };

  return {
    preferences: getPreferences(),
    updateTaskDetails,
    updateTaskManagement,
    updateUI,
    clearAll: clearPreferences,
  };
};

// Key for last viewed project
const LAST_VIEWED_PROJECT_KEY = 'lastViewedProjectId';

export function setLastViewedProjectId(projectId: string | null) {
  try {
    if (projectId) {
      localStorage.setItem(LAST_VIEWED_PROJECT_KEY, projectId);
    } else {
      localStorage.removeItem(LAST_VIEWED_PROJECT_KEY);
    }
  } catch {}
}

export function getLastViewedProjectId(): string | null {
  try {
    return localStorage.getItem(LAST_VIEWED_PROJECT_KEY);
  } catch {
    return null;
  }
}
